import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { createScheduledMessage } from '@/lib/api';

interface ScheduleMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  areaId: string;
  threadId?: string;
  content: string;
  onScheduled: () => void;
}

export function ScheduleMessageDialog({
  open,
  onOpenChange,
  areaId,
  threadId,
  content,
  onScheduled,
}: ScheduleMessageDialogProps) {
  const [scheduledFor, setScheduledFor] = useState(() => {
    // Default: 1 hour from now, rounded to nearest 15min
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    return toLocalDatetimeString(d);
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSchedule = async () => {
    if (!content.trim()) {
      setError('Message cannot be empty');
      return;
    }
    const date = new Date(scheduledFor);
    if (date <= new Date()) {
      setError('Must be in the future');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await createScheduledMessage({
        areaId,
        threadId,
        content: content.trim(),
        scheduledFor: date.toISOString(),
      });
      onScheduled();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <Dialog.Title className="text-lg font-semibold">Schedule Message</Dialog.Title>

          <div className="mt-4 space-y-3">
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {content.length > 100 ? content.slice(0, 100) + '...' : content}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Send at
              </label>
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                min={toLocalDatetimeString(new Date())}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
              Cancel
            </Dialog.Close>
            <button
              onClick={handleSchedule}
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function toLocalDatetimeString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
